import React, {FC, useEffect, useRef} from 'react';

import {useCards} from './useCards';

import * as styles from './styles.module.scss';

export const PricingList: FC = () => {
	const trackRef = useRef<HTMLDivElement>(null);
	const dotsRef = useRef<HTMLDivElement[]>([]);
	const {cards} = useCards();

	// Handle dots - likely should refactor in the future
	useEffect(() => {
		const track = trackRef.current;
		const dots = dotsRef.current.filter(Boolean);
		if (!track || dots.length === 0) return;

		const slides = [...track.children] as HTMLElement[];
		if (slides.length === 0) return;
		const minVisible = 0.6;

		// Track visibility state of all slides
		const isVisible = new Array(slides.length).fill(false);

		// Update active dots for all visible slides
		const io = new IntersectionObserver((entries) => {
			// Update visibility state for changed entries
			entries.forEach(entry => {
				const index = slides.indexOf(entry.target as HTMLElement);
				if (index !== -1) {
					isVisible[index] = entry.intersectionRatio > minVisible;
				}
			});

			// Remove active class from all dots first
			dots.forEach(dot => dot?.classList.remove(styles.active));

			// Add active class to dots for all currently visible slides
			isVisible.forEach((visible, index) => {
				if (visible && dots[index]) {
					dots[index].classList.add(styles.active);
				}
			});
		}, {root: track, threshold: minVisible});

		slides.forEach(slide => io.observe(slide));

		// Cleanup
		return () => {
			io.disconnect();
		};
	}, [cards]);

	// Auto-scroll container to the second card on initial load
	useEffect(() => {
		if (!trackRef.current || cards?.length < 2) return;

		const container = trackRef.current;
		const card = container.children[1] as HTMLElement; // second card

		// How far left so the card’s center lines up with the container’s center?
		const left = card.offsetLeft // card’s left edge
    		- container.offsetLeft // minus container padding/offset
    		- (container.clientWidth - card.clientWidth) / 2; // nudge so *centers* align

		container.scrollTo({left, behavior: 'smooth'}); // y stays untouched
	}, [cards]);

	// No cards to display, just show empty (wrapper will handle)
	if (cards.length === 0) {
		return null;
	}

	return (
		<div className={styles.wrapper}>
			{/* Scrollable track */}
			<div ref={trackRef} className={styles.carousel}>
				{cards}
			</div>

			{/* Pagination dots */}
			<nav className={styles.dots}>
				{cards.map((_, index) => (
					<div
						key={index}
						ref={(el) => {
							if (el) dotsRef.current[index] = el;
						}}
						className={styles.dot}
					/>
				))}
			</nav>
		</div>
	);
};
